/**
 * Default layout for the Dashboard page.
 *
 * Matches the Urban Unity dashboard composition:
 * - 3 MetricCards (Cash Collected, Revenue, Close Rate)
 * - 4 StatCards (Show Rate, Calls Due, Calls Taken, Calls Closed)
 * - 4 StatCards (Avg Cash/Call, Cash/Close, Cash/Application, Cash/Opt-in)
 * - 2 StatCards (Total Cash Applications, Total Cash Opt-ins)
 * - 5 AdditionalStats (New Cash, MRR, Gross Revenue, Total Applications, Total Opt-ins)
 * - 1 full-width pieChart (Country)
 * - 1 areaChart (Cash Trend) + 1 pieChart (Lead Source) side-by-side
 * - 1 full-width pieChart (Content)
 * - 1 full-width table (Recent Calls)
 */

import type { DashboardLayout } from "../types";

export const DASHBOARD_DEFAULT: DashboardLayout = {
  widgets: [
    // --- Row 1: Metric Cards (y:0) ---
    {
      id: "dash-metric-cash",
      type: "metric",
      locked: true,
      title: "Cash Collected",
      config: {
        metricKey: "totalCashCollected",
        format: "currencyAbbreviated",
        icon: "dollar",
        color: "green",
        sparklineKey: "cashTrend",
      },
    },
    {
      id: "dash-metric-revenue",
      type: "metric",
      locked: true,
      title: "Revenue",
      config: {
        metricKey: "totalRevenue",
        format: "currencyAbbreviated",
        icon: "trending",
        color: "red",
        sparklineKey: "cashTrend",
      },
    },
    {
      id: "dash-metric-closerate",
      type: "metric",
      locked: true,
      title: "Close Rate",
      config: {
        metricKey: "closeRate",
        format: "percent",
        icon: "target",
        color: "blue",
      },
    },

    // --- Row 2: Call Stat Cards (y:3) ---
    {
      id: "dash-stat-showrate",
      type: "stat",
      title: "Show Rate",
      config: { metricKey: "showRate", format: "percent" },
    },
    {
      id: "dash-stat-callsdue",
      type: "stat",
      title: "Calls Due",
      config: { metricKey: "callsDue", format: "number" },
    },
    {
      id: "dash-stat-callstaken",
      type: "stat",
      title: "Calls Taken",
      config: { metricKey: "callsTaken", format: "number" },
    },
    {
      id: "dash-stat-callsclosed",
      type: "stat",
      title: "Calls Closed",
      config: { metricKey: "callsClosed", format: "number" },
    },

    // --- Row 3: Average Stat Cards (y:5) ---
    {
      id: "dash-stat-avgcashcall",
      type: "stat",
      title: "Avg. Cash/Call",
      config: { metricKey: "avgCashPerCall", format: "currency" },
    },
    {
      id: "dash-stat-avgcashclose",
      type: "stat",
      title: "Avg. Cash/Close",
      config: { metricKey: "avgCashPerClose", format: "currency" },
    },
    {
      id: "dash-stat-avgcashapp",
      type: "stat",
      title: "Avg. Cash/Application",
      config: { metricKey: "avgCashPerApplication", format: "currency" },
    },
    {
      id: "dash-stat-avgcashoptin",
      type: "stat",
      title: "Avg. Cash/Opt-in",
      config: { metricKey: "avgCashPerOptin", format: "currency" },
    },

    // --- Row 4: Total Cash Breakdown (y:7) ---
    {
      id: "dash-stat-totalcashapp",
      type: "stat",
      title: "Total Cash (Applications)",
      config: { metricKey: "totalApplicationsCash", format: "currency" },
    },
    {
      id: "dash-stat-totalcashoptin",
      type: "stat",
      title: "Total Cash (Opt-ins)",
      config: { metricKey: "totalOptinsCash", format: "currency" },
    },

    // --- Row 5: Additional Stats (y:9) ---
    {
      id: "dash-addstat-newcash",
      type: "additionalStat",
      title: "New Cash Collected",
      config: {
        metricKey: "newCashCollected",
        format: "currency",
        description: "Total cash from new deals (excludes MRR)",
      },
    },
    {
      id: "dash-addstat-mrr",
      type: "additionalStat",
      title: "MRR",
      config: {
        metricKey: "mrrCash",
        format: "currency",
        description: "Monthly recurring revenue",
      },
    },
    {
      id: "dash-addstat-grossrev",
      type: "additionalStat",
      title: "Total Gross Revenue",
      config: {
        metricKey: "totalGrossRevenue",
        format: "currency",
        description: "Total contract value (all revenue)",
      },
    },
    {
      id: "dash-addstat-totalapps",
      type: "additionalStat",
      title: "Total Applications",
      config: {
        metricKey: "totalApplications",
        format: "number",
      },
    },
    {
      id: "dash-addstat-totaloptins",
      type: "additionalStat",
      title: "Total Opt-ins",
      config: {
        metricKey: "totalOptins",
        format: "number",
      },
    },
    // --- Charts ---
    {
      id: "dash-pie-country",
      type: "pieChart",
      title: "Closes by Country",
      config: {
        dataKey: "countryData",
        titleClosed: "Closes by Country",
        titleAll: "Leads by Country",
        leadModeAware: true,
      },
    },
    {
      id: "dash-area-cashtrend",
      type: "areaChart",
      title: "Cash Collected Trend",
      config: {
        dataKey: "cashTrend",
        title: "Cash Collected Trend",
        yFormat: "currency",
      },
    },
    {
      id: "dash-pie-leadsource",
      type: "pieChart",
      title: "Closes By Lead Source",
      config: {
        dataKey: "leadSourceData",
        titleClosed: "Closes by Lead Source",
        titleAll: "Leads by Lead Source",
        leadModeAware: true,
      },
    },
    {
      id: "dash-pie-content",
      type: "pieChart",
      title: "Closes by Content",
      config: {
        dataKey: "contentData",
        titleClosed: "Closes by Content",
        titleAll: "Leads by Content",
        leadModeAware: true,
      },
    },
    {
      id: "dash-table-recentcalls",
      type: "table",
      title: "Recent Calls",
      config: {
        dataKey: "recentCalls",
        title: "Recent Calls",
        search: {
          enabled: true,
          placeholder: "Search lead name, email, phone...",
          keys: ["leadName", "email", "phone"],
        },
        filters: [
          { key: "status", label: "Status" },
          { key: "closer", label: "Closer" },
          { key: "setter", label: "Setter" },
          { key: "callOutcome", label: "Call Outcome" },
        ],
      },
    },
  ],

  gridLayout: [
    // Row 1: 3 metric cards (h=3)
    { i: "dash-metric-cash", x: 0, y: 0, w: 4, h: 3 },
    { i: "dash-metric-revenue", x: 4, y: 0, w: 4, h: 3 },
    { i: "dash-metric-closerate", x: 8, y: 0, w: 4, h: 3 },

    // Row 2: 4 stat cards (h=2)
    { i: "dash-stat-showrate", x: 0, y: 3, w: 3, h: 2 },
    { i: "dash-stat-callsdue", x: 3, y: 3, w: 3, h: 2 },
    { i: "dash-stat-callstaken", x: 6, y: 3, w: 3, h: 2 },
    { i: "dash-stat-callsclosed", x: 9, y: 3, w: 3, h: 2 },

    // Row 3: 4 stat cards — averages (h=2)
    { i: "dash-stat-avgcashcall", x: 0, y: 5, w: 3, h: 2 },
    { i: "dash-stat-avgcashclose", x: 3, y: 5, w: 3, h: 2 },
    { i: "dash-stat-avgcashapp", x: 6, y: 5, w: 3, h: 2 },
    { i: "dash-stat-avgcashoptin", x: 9, y: 5, w: 3, h: 2 },

    // Row 4: 2 stat cards — total cash breakdown (h=2)
    { i: "dash-stat-totalcashapp", x: 0, y: 7, w: 6, h: 2 },
    { i: "dash-stat-totalcashoptin", x: 6, y: 7, w: 6, h: 2 },

    // Row 5: 5 additional stats (h=3)
    { i: "dash-addstat-newcash", x: 0, y: 9, w: 2, h: 3 },
    { i: "dash-addstat-mrr", x: 2, y: 9, w: 2, h: 3 },
    { i: "dash-addstat-grossrev", x: 4, y: 9, w: 2, h: 3 },
    { i: "dash-addstat-totalapps", x: 6, y: 9, w: 3, h: 3 },
    { i: "dash-addstat-totaloptins", x: 9, y: 9, w: 3, h: 3 },

    // Row 6: full-width pie chart (Country)
    { i: "dash-pie-country", x: 0, y: 12, w: 12, h: 14 },

    // Row 7: area chart + pie chart side-by-side
    { i: "dash-area-cashtrend", x: 0, y: 26, w: 6, h: 12 },
    { i: "dash-pie-leadsource", x: 6, y: 26, w: 6, h: 12 },

    // Row 8: full-width pie chart (Content)
    { i: "dash-pie-content", x: 0, y: 38, w: 12, h: 16 },

    // Row 9: full-width table (Recent Calls)
    { i: "dash-table-recentcalls", x: 0, y: 54, w: 12, h: 16 },
  ],
};
